import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { N10 } from '@atlaskit/theme/colors';
import {
  CurrencyISOCode,
  FormattedPriceCalculationResponse
} from '@atlassiansox/bxp-use-pricing';
import styled from '@emotion/styled';
import React, { useEffect, useState } from 'react';
import { useDesktopMediaQuery } from '../../hooks';
import { deviceMediaQueries, TRACKING_EVENT_NAMES } from '../../utils';
import { BillingPeriodSelector } from '../billing-period-selector';
import { UnitCount } from '../unit-count';
import { PricingExampleContainerProps } from './pricing-example-container';
import { PricingExample } from './pricing-example-states';
import { PricingExampleButton } from './pricing-example-button';
import { FormattedMessage } from 'react-intl';

export type PricingExampleDefaultProps = Omit<
  PricingExampleContainerProps,
  'hideBillingSelector'
> & {
  priceCalc: FormattedPriceCalculationResponse;
  currencyISOCode: CurrencyISOCode;
};

const PricingExampleDefault = ({
  unitTrackingTerm,
  pricingExampleUnitCounts,
  priceCalc,
  showFirstFlatRateTier,
  currencyISOCode
}: PricingExampleDefaultProps) => {
  const [showExample, setShowExample] = useState(false);
  const { createAnalyticsEvent } = useAnalyticsEvents();
  const isDesktop = useDesktopMediaQuery();

  // send analytics event if pricing example has been shown
  useEffect(() => {
    const analyticsEvent = createAnalyticsEvent({
      name: !showExample
        ? TRACKING_EVENT_NAMES.showPricingExampleTriggered
        : TRACKING_EVENT_NAMES.hidePricingExampleTriggered
    });
    analyticsEvent.fire();
  }, [showExample]);

  return (
    <BoxShadowContainer>
      <SelectorGridContainer>
        <UnitCount unitTrackingTerm={unitTrackingTerm} />

        <BillingPeriodSelector
          exampleShown={showExample}
          onShowHideExampleClick={(setExampleShown) =>
            setShowExample(setExampleShown)
          }
        />
        {!showExample && !isDesktop && (
          <ExampleLabel>
            <PricingExampleButton
              onClickHandler={() => {
                setShowExample(true);
              }}
            >
              <FormattedMessage
                id="showExampleLabel"
                defaultMessage="See pricing example"
                description="Label for button to show pricing example."
              />
            </PricingExampleButton>
          </ExampleLabel>
        )}
        {showExample && (
          <PricingExample
            priceCalc={priceCalc}
            unitCounts={pricingExampleUnitCounts}
            showFirstFlatRateTier={showFirstFlatRateTier}
            currencyISOCode={currencyISOCode}
          />
        )}
        {showExample && !isDesktop && (
          <ExampleLabel>
            <PricingExampleButton
              onClickHandler={() => {
                setShowExample(false);
              }}
            >
              <FormattedMessage
                id="hideExampleLabel"
                defaultMessage="Hide pricing example"
                description="Label for hide button to close the example display."
              />
            </PricingExampleButton>
          </ExampleLabel>
        )}
      </SelectorGridContainer>
    </BoxShadowContainer>
  );
};
export const GridContainer = `
  display: grid;
  grid-template-columns: 1fr repeat(4, minmax(max-content, auto)) 1fr;
  ${deviceMediaQueries.tablet} {
    display: block;
    margin: auto;
    max-width: 400px;
  }
`;

const SelectorGridContainer = styled.div`
  ${GridContainer}
  grid-template-rows: minmax(max-content, auto) 20px minmax(max-content, auto) 1fr;
`;

export const BoxShadowContainer = styled.div`
  align-items: center;
  background-color: ${N10};
  border-radius: 4px;
  box-shadow: 1px 1px 6px rgba(0, 0, 0, 0.1);
  padding: 20px;
`;

const ExampleLabel = styled.div`
  button {
    margin: 0px;
    padding: 0px;
  }
`;

export { PricingExampleDefault };
